/**
 * Limits of the backend analytics inputs (FE-321). One field over its limit
 * makes the backend refuse the whole call, so values are trimmed here.
 */
export const ANALYTICS_LIMITS = {
  nameLength: 64,
  urlLength: 2048,
  titleLength: 512,
  /** utm values, click ids and other free text. */
  textLength: 512,
  propsBytes: 4 * 1024,
  eventsPerBatch: 50
} as const

/** Event props as the GA4 connector forwards them: flat scalars. */
export type AnalyticsProps = Record<string, string | number | boolean>

/** Query params that carry OAuth codes, tokens and states. */
const SECRET_PARAMS = new Set([
  'code',
  'token',
  'state',
  'access_token',
  'id_token'
])
const SECRET_PARAM_PREFIX = 'privy_oauth_'

const isSecretParam = (key: string) => {
  const lower = key.toLowerCase()

  return SECRET_PARAMS.has(lower) || lower.startsWith(SECRET_PARAM_PREFIX)
}

export const trimText = (
  value: string,
  maxLength: number = ANALYTICS_LIMITS.textLength
) => value.slice(0, maxLength)

/** UTF-8 size, which is what the backend measures for props and bodies. */
export const byteLength = (value: string) =>
  new TextEncoder().encode(value).length

/**
 * The leading items whose JSON fits into `maxBytes`, never fewer than one:
 * an oversized item is still sent (and refused) rather than stuck in front.
 */
export const cutToBytes = <T>(items: T[], maxBytes: number) => {
  let bytes = byteLength(JSON.stringify(items[0] ?? null))
  let count = Math.min(items.length, 1)
  while (count < items.length) {
    bytes += byteLength(JSON.stringify(items[count]))
    if (bytes > maxBytes) break
    count += 1
  }

  return items.slice(0, count)
}

/**
 * Drops what must not leave the browser from a URL: the hash and the OAuth
 * codes, tokens and states (`code`, `token`, `state`, `privy_oauth_*` …),
 * then trims it to `ANALYTICS_LIMITS.urlLength`. A string that is not a URL
 * is only trimmed.
 */
export const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url)
    parsed.hash = ''
    const secrets = [...parsed.searchParams.keys()].filter(isSecretParam)
    secrets.forEach(key => parsed.searchParams.delete(key))

    return trimText(parsed.href, ANALYTICS_LIMITS.urlLength)
  } catch {
    return trimText(url, ANALYTICS_LIMITS.urlLength)
  }
}

const toScalar = (value: unknown) => {
  if (typeof value === 'string') return trimText(value)
  if (typeof value === 'boolean') return value

  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Keeps finite numbers, booleans and trimmed strings; anything else is left
 * out. `null` when the result is still over `ANALYTICS_LIMITS.propsBytes`:
 * the backend would drop such an event anyway.
 */
export const sanitizeProps = (props: object): AnalyticsProps | null => {
  const clean: AnalyticsProps = {}

  Object.entries(props).forEach(([key, value]) => {
    const scalar = toScalar(value)
    if (scalar !== null) clean[key] = scalar
  })

  return byteLength(JSON.stringify(clean)) > ANALYTICS_LIMITS.propsBytes ?
      null
    : clean
}
